import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { AlertTriangle, SearchX, Banknote, Users, Clock, ClipboardList, TrendingDown, MessageSquare } from 'lucide-react';

const tenantProblems = [
  {
    icon: SearchX,
    title: 'No Digital Trail',
    description: 'Cash payments lead to disputes and lost records',
    color: 'from-red-500/20 to-orange-500/20',
    iconColor: 'text-red-400',
  },
  {
    icon: Banknote,
    title: 'Deposit Theft',
    description: 'No documentation means you lose deposits unfairly',
    color: 'from-orange-500/20 to-yellow-500/20',
    iconColor: 'text-orange-400',
  },
  {
    icon: AlertTriangle,
    title: 'No Safety Net',
    description: 'No access to affordable insurance coverage',
    color: 'from-yellow-500/20 to-amber-500/20',
    iconColor: 'text-yellow-400',
  },
  {
    icon: Users,
    title: 'No Community',
    description: 'Isolated with no peer support or advice',
    color: 'from-amber-500/20 to-red-500/20',
    iconColor: 'text-amber-400',
  },
];

const managerProblems = [
  {
    icon: TrendingDown,
    title: 'High Delinquency',
    description: '30-40% of rent paid late or never',
    color: 'from-purple-500/20 to-pink-500/20',
    iconColor: 'text-purple-400',
  },
  {
    icon: ClipboardList,
    title: 'Manual Processes',
    description: 'Rely on WhatsApp, spreadsheets, and paper',
    color: 'from-pink-500/20 to-rose-500/20',
    iconColor: 'text-pink-400',
  },
  {
    icon: Users,
    title: 'High Turnover',
    description: 'Slow maintenance drives tenants away',
    color: 'from-rose-500/20 to-red-500/20',
    iconColor: 'text-rose-400',
  },
  {
    icon: Clock,
    title: 'Time Consuming',
    description: 'Hours wasted on collection and admin',
    color: 'from-red-500/20 to-purple-500/20',
    iconColor: 'text-red-400',
  },
];

const ProblemCard = ({ problem, idx, type }) => (
  <motion.div
    className="relative group"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: idx * 0.1 }}
    viewport={{ once: true }}
  >
    <div className={`absolute inset-0 bg-gradient-to-br ${problem.color} rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
    <div className="relative card h-full">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${problem.color} flex items-center justify-center mb-4`}>
        <problem.icon className={problem.iconColor} size={24} />
      </div>
      <h4 className="text-lg font-bold text-white mb-2">{problem.title}</h4>
      <p className="text-sm text-gray-400">{problem.description}</p>
    </div>
  </motion.div>
);

export default function ProblemsSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const orb1X = useTransform(scrollYProgress, [0, 1], ['-15%', '15%']);
  const orb2X = useTransform(scrollYProgress, [0, 1], ['15%', '-15%']);

  return (
    <section ref={ref} className="py-24 relative overflow-hidden">
      {/* Background orbs with parallax */}
      <motion.div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-red-500/10 to-transparent rounded-full blur-3xl pointer-events-none" style={{ x: orb1X }}></motion.div>
      <motion.div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-orange-500/10 to-transparent rounded-full blur-3xl pointer-events-none" style={{ x: orb2X }}></motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-medium mb-6">
            The Problem
          </span>
          <h2 className="section-title">
            The rental market in{' '}
            <span className="text-gradient-warm">Africa is broken.</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto mt-4">
            Despite being valued at over <span className="text-white font-semibold">$50 billion annually</span>, 
            the rental market operates on cash, phone calls, and manual processes. YAWOTE was built to fix this.
          </p>
        </motion.div>

        {/* Two Column Problem Grid */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Tenant Problems */}
          <div>
            <motion.div
              className="flex items-center gap-3 mb-8"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Users className="text-white" size={20} />
              </div>
              <h3 className="text-2xl font-bold text-white">For Tenants</h3>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-4">
              {tenantProblems.map((problem, idx) => (
                <ProblemCard key={idx} problem={problem} idx={idx} type="tenant" />
              ))}
            </div>
          </div>

          {/* Manager Problems */}
          <div>
            <motion.div
              className="flex items-center gap-3 mb-8"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <ClipboardList className="text-white" size={20} />
              </div>
              <h3 className="text-2xl font-bold text-white">For Property Managers</h3>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-4">
              {managerProblems.map((problem, idx) => (
                <ProblemCard key={idx} problem={problem} idx={idx} type="manager" />
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-400 text-lg">
            It doesn't have to be this way.{' '}
            <span className="text-blue-400 font-semibold">YAWOTE changes everything.</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
